import { Box } from "@mui/material";
import { App, Plugin, Route, View } from "@pimo/pimo-app-builder";
import { DragDropLayoutTabDefinition } from "@pimo/pimo-components";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React from "react";
import { APP_RECORD_TYPES } from "vmd-types";
import { canUserPerformOperation } from "vmd-utils";

import { TestAppState } from "../app";
import { APP_ROUTES } from "../constants";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import { dropStaleLayoutComponents } from "../utils/drop-stale-layout-components";
import { isRecordTypeVisible } from "../utils/filter-reporting-items-by-record-type";
import { DASHBOARD_CARDS } from "./dashboard";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DashboardPluginState {}

const STALE_DASHBOARD_COMPONENT_IDS = new Set<string>(
  isRecordTypeVisible(APP_RECORD_TYPES.IT_SERVICE)
    ? []
    : [
        "criticalityITServicesDashboardComponent",
        "numberOfITServicesDashboardComponent",
      ]
);

export class DashboardPlugin implements Plugin<TestAppState> {
  private view?: View<TestAppState, DragAndDropLayout>;
  route?: Route;

  private app?: App<TestAppState>;
  // private downloadMenuAnchor?: HTMLElement | null;

  onRegister(app: App<TestAppState>) {
    this.app = app;
    this.view = app.createView({
      name: "dashboard",
      layout: new DragAndDropLayout(),
    });

    this.view.mapLayoutState(
      ({ dashboardLayout, userProfile, language, settings }) => {
        let tabs;
        if (dashboardLayout?.tabs && dashboardLayout.tabs.length > 0) {
          tabs = dashboardLayout.tabs;
        } else {
          tabs = [
            {
              id: 0,
              title: getLocalizedMessage("en", "dashboard"),
              rows: [
                {
                  id: 0,
                  columnCount: 1,
                  columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                },
              ],
            },
          ];
        }
        tabs = dropStaleLayoutComponents(tabs, STALE_DASHBOARD_COMPONENT_IDS);

        return {
          canEdit: canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          }),
          isComponentEditingEnabled: true,
          displayTabs: dashboardLayout?.displayTabs ?? true,
          language: language || "en",
          tabs,
          rightSlot: React.createElement(
            Box,
            {
              className: "no-print",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }
            // Hide download button until a proper download template is implemented
            // React.createElement(
            //   IconButton,
            //   {
            //     onClick: (event: React.MouseEvent<HTMLElement>) => {
            //       this.downloadMenuAnchor = event.currentTarget;
            //       this.app?.patchAppState({});
            //     },
            //     sx: { ml: 1 },
            //   },
            //   React.createElement(FileDownloadOutlined)
            // ),
            // React.createElement(
            //   Menu,
            //   {
            //     anchorEl: this.downloadMenuAnchor,
            //     open: Boolean(this.downloadMenuAnchor),
            //     onClose: () => {
            //       this.downloadMenuAnchor = null;
            //       this.app?.patchAppState({});
            //     },
            //   },
            //   React.createElement(
            //     MenuItem,
            //     {
            //       onClick: () => {
            //         setTimeout(() => {
            //           window.print();
            //         }, 1000);
            //         this.downloadMenuAnchor = null;
            //         this.app?.patchAppState({});
            //       },
            //     },
            //     "Download PDF"
            //   )
            // )
          ),
        };
      }
    );

    this.view.on("componentEdited", async ({ payload }) => {
      if (!payload) {
        return;
      }

      const componentEditedPayload = payload as {
        componentId: string;
        componentValue: Record<string, unknown>;
      };

      if (
        !componentEditedPayload.componentId ||
        !componentEditedPayload.componentValue
      ) {
        return;
      }

      const { componentId, componentValue } = componentEditedPayload;
      this.app?.patchAppState({
        dashboardComponentValues: {
          ...this.app?.getAppState()?.dashboardComponentValues,
          [componentId]: componentValue,
        },
      });
    });

    this.buildDashboardView();

    this.route = app.createRoute({
      path: APP_ROUTES.dashboard,
      view: this.view,
    });

    this.view.on("updateLayout", async ({ payload }) => {
      if (!payload || !this.app) {
        return;
      }

      const { dashboardLayout } = this.app.getAppState();
      const currentDisplayTabs = dashboardLayout?.displayTabs ?? true;
      const { tabs } = payload as { tabs: DragDropLayoutTabDefinition[] };

      this.app.patchAppState({
        dashboardLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      await createViewConfiguration({
        tabs,
        canEdit: dashboardLayout?.canEdit ?? true,
        displayTabs: currentDisplayTabs,
        type: "dashboard",
      });
    });

    this.view?.on("startLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: false,
      });
    });

    this.view?.on("stopLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: true,
      });
    });

    this.view.buildDynamicallyBasedOnAppState(
      (appState) => ({
        configuration: appState.settings?.configuration,
        dashboard: appState.dashboard,
      }),
      () => {
        this.buildDashboardView();
      }
    );
  }

  buildDashboardView() {
    if (!this.app || !this.view) {
      return;
    }

    // One call per card, in layout order. Each lives in ./dashboard/cards and
    // owns its own `addComponent` and `mapState`; none of them reads anything
    // the others define, which is why they could be lifted out one for one.
    // The order lives in DASHBOARD_CARDS, beside the cards themselves, so a
    // spec can assert it — see dashboard/dashboard.structure.spec.ts.
    for (const addCard of DASHBOARD_CARDS) {
      addCard(this.view, this.app);
    }
  }
}
